import { CheckCircleFilled, Close, EmergencyHomeFilled, InfoFilled, WarningFilled } from '@interhyp/platform-icons';
import Tippy from '@tippyjs/react/headless';
import classnames from 'classnames';
import { FocusTrap } from 'focus-trap-react';
import React, { useRef } from 'react';
import { IconRenderer } from "../../helper/components/IconRenderer.js";
import { createIntervalPromise } from "../../helper/functions/createIntervalPromise.js";
import { useClientSideRendering } from "../../helper/hooks/useClientSideRendering.js";
import { useStableId } from "../../helper/hooks/useStableId.js";
import { IconButton } from "../iconButton/IconButton.js";
import { Typography } from "../typography/Typography.js";
import styles from "./Tooltip.module.js";
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
const ICONS = {
  error: EmergencyHomeFilled,
  warning: WarningFilled,
  success: CheckCircleFilled,
  info: InfoFilled
};

// Recommendations from UX in milliseconds
export const RECOMMENDED_TOOLTIP_FADE_IN_DELAY = 400;
export const RECOMMENDED_TOOLTIP_FADE_OUT_DELAY = 500;
export const RECOMMENDED_TOOLTIP_DELAY = [RECOMMENDED_TOOLTIP_FADE_IN_DELAY, RECOMMENDED_TOOLTIP_FADE_OUT_DELAY];
export const Tooltip = ({
  interactive,
  children,
  iconRef,
  className,
  content,
  title,
  onCloseClick,
  onClickOutside,
  open,
  autoWidth = false,
  placement = 'auto',
  fallbackPlacements = [],
  offsetX = 5,
  offsetY = 17,
  zIndex = 1010,
  appendTo,
  triggerTarget,
  delay,
  type,
  noStatusIcon = false,
  'data-testid': dataTestId
}) => {
  const childRef = useRef(null);
  const typeClasses = {
    [styles.info]: type === 'info',
    [styles.error]: type === 'error',
    [styles.warning]: type === 'warning',
    [styles.success]: type === 'success'
  };
  const classes = classnames(styles.tooltip, className, typeClasses, {
    [styles.autoWidth]: autoWidth
  });
  const arrowContainerClasses = classnames(styles.arrowContainer, typeClasses);
  const isInteractive = interactive ?? (typeof open !== 'undefined' && typeof onCloseClick !== 'undefined');
  const showCloseIcon = typeof onCloseClick !== 'undefined';
  const isClientSideRendering = useClientSideRendering();
  const id = useStableId();
  return /*#__PURE__*/_jsx(Tippy, {
    reference: iconRef ? iconRef : childRef,
    offset: [offsetX, offsetY],
    placement: placement,
    visible: open,
    onClickOutside: onClickOutside,
    interactive: isInteractive,
    appendTo: isClientSideRendering ? appendTo || document.querySelector('#root') || document.body || 'parent' : appendTo,
    zIndex: zIndex,
    triggerTarget: triggerTarget,
    delay: delay,
    popperOptions: placement !== 'auto' ? {
      placement,
      modifiers: [{
        name: 'flip',
        enabled: fallbackPlacements.length > 0,
        options: {
          fallbackPlacements
        }
      }]
    } : {},
    render: attrs => /*#__PURE__*/_jsx(FocusTrap, {
      active: Boolean(open && (showCloseIcon || interactive)),
      focusTrapOptions: {
        fallbackFocus: `[id="${id}"]`,
        checkCanFocusTrap: a => createIntervalPromise(() => a.some(elm => elm.id === id)),
        allowOutsideClick: true
      },
      children: /*#__PURE__*/_jsxs("div", {
        id: id,
        className: classes,
        tabIndex: -1,
        "data-testid": dataTestId,
        ...attrs,
        children: [(title || showCloseIcon) && /*#__PURE__*/_jsxs("header", {
          className: classnames(styles.header, {
            [styles.hasTitle]: !!title || typeof type !== 'undefined' && !noStatusIcon
          }),
          children: [/*#__PURE__*/_jsxs("span", {
            children: [typeof type !== 'undefined' && !noStatusIcon && /*#__PURE__*/_jsx(IconRenderer, {
              icon: ICONS[type],
              className: styles.titleIcon
            }), title && /*#__PURE__*/_jsx(Typography, {
              variant: "h4",
              margin: false,
              children: title
            })]
          }), showCloseIcon && /*#__PURE__*/_jsx(IconButton, {
            className: styles.closeButton,
            icon: Close,
            onClick: onCloseClick,
            toolbar: true,
            tertiary: true
          })]
        }), /*#__PURE__*/_jsx("div", {
          className: classnames(styles.contentContainer, {
            [styles.iconSpacing]: showCloseIcon && !title
          }),
          children: content
        }), /*#__PURE__*/_jsx("div", {
          "data-popper-arrow": "",
          className: arrowContainerClasses
        })]
      })
    }),
    children: iconRef || !children ? /*#__PURE__*/_jsx(_Fragment, {
      children: children
    }) : /*#__PURE__*/_jsx("span", {
      className: styles.iconContainer,
      ref: childRef,
      children: children
    })
  });
};